import { Box, Checkbox } from '@mui/material'
import { ReactComponent as CheckBox } from 'assets/icons/checkBox.svg'
import CheckBoxActive from 'assets/icons/checkBoxActive'
import { morphForWords, roundingNumber } from 'components/shared/processes'
import {
	buildPlaceholderAxis,
	hasChartSeriesData,
} from 'components/shared/processes/chartNoDataSeries'
import { LamenessTypeChart } from 'models'
import moment from 'moment'
import ReactApexChart from 'react-apexcharts'
import { COLORS } from 'styles/colors'
import ChartLegend from 'ui/charts/legends/ChartLegend'
import { TypographySecondary14 } from 'ui/typography/TypographySecondary14'
import { localizationApexCharts } from 'utils'
import {
	getColorByScoreForCrytical,
	getSeriesData,
	useVisibleSeries,
} from './processes'

const LamenessChart = ({ dotsInfo, selectTypeOfChart }) => {
	const [visibleSeries, handleCheckboxChange] = useVisibleSeries(dotsInfo)
	const isEmptyData = !hasChartSeriesData(dotsInfo.map(el => el.category ?? []))

	const isSingleDay = dotsInfo[0]?.timestamp?.length === 1
	const normalizedDotsInfo = isSingleDay
		? dotsInfo.map(el => ({
				...el,
				category: [el.category[0], el.category[0]],
				timestamp: [el.timestamp[0], el.timestamp[0]],
			}))
		: dotsInfo

	const series = getSeriesData(
		normalizedDotsInfo,
		visibleSeries,
		selectTypeOfChart,
	)
	const timestamp = normalizedDotsInfo[0]?.timestamp ?? []
	const placeholderAxis = isEmptyData ? buildPlaceholderAxis(timestamp) : null
	const chartTimestamps = placeholderAxis?.timestamps ?? timestamp
	const chartSeries = isEmptyData
		? [
				{
					name: 'Нет данных',
					data: (placeholderAxis?.timestamps ?? []).map((x, index) => ({
						x,
						y: placeholderAxis?.values[index] ?? 0,
					})),
					color: COLORS.inputBorder,
				},
			]
		: series
	const seriesColors = chartSeries.map(item => item.color)
	const options: any = {
		mask: false,
		colors: seriesColors,
		chart: {
			animations: {
				enabled: !isEmptyData,
				easing: 'easeout',
				speed: 200,
				animateGradually: {
					enabled: true,
					delay: 100,
				},
				dynamicAnimation: {
					enabled: true,
					speed: 200,
				},
			},
			locales: localizationApexCharts(),
			defaultLocale: 'ru',
			zoom: {
				enabled: false,
			},
			height: 250,
			type: 'line',
			toolbar: {
				show: false,
			},
			connectNulls: false,
		},
		markers: {
			size: 0,
			colors: ['#fff'],
			strokeColors: seriesColors,
			strokeWidth: 2,
			hover: {
				size: 5,
			},
		},
		stroke: {
			width: isEmptyData ? 1.5 : 2,
			curve: 'straight',
			lineCap: isEmptyData ? 'round' : 'butt',
		},
		title: {
			style: {
				fontSize: '12px',
				fontFamily: 'Montserrat',
				fontStyle: 'normal',
				fontWeight: 400,
				lineHeight: '15px',
				color: '#7F7F84',
			},
			text: 'Количество голов',
		},

		dataLabels: {
			enabled: false,
		},
		grid: {
			strokeDashArray: [6, 3],
			show: true,
			xaxis: {
				lines: {
					show: true,
				},
			},
		},
		xaxis: {
			crosshairs: {
				show: true,
				opacity: 1,
				position: 'back',
				stroke: {
					width: 1,
					dashArray: 0,
				},
			},
			type: isEmptyData ? 'datetime' : 'category',
			tickAmount: isEmptyData ? Math.max(timestamp.length - 1, 1) : undefined,
			tooltip: {
				enabled: false,
			},

			labels: {
				formatter: function (value) {
					return moment(value).format('DD.MM')
				},
				datetimeUTC: false,
				style: {
					fontSize: '12px',
					fontFamily: 'Montserrat',
					fontStyle: 'normal',
					fontWeight: 400,
					lineHeight: '15px',
					color: '#7F7F84',
				},
			},
			categories: chartTimestamps,
		},
		yaxis: {
			tickAmount: 4,
			min: 0,
			max: isEmptyData
				? 1
				: function (max) {
						return roundingNumber(max * 1.2, 0)
					},
			labels: {
				style: {
					fontSize: '12px',
					fontFamily: 'Montserrat',
					fontStyle: 'normal',
					fontWeight: 400,
					lineHeight: '15px',
					color: '#7F7F84',
				},
				formatter: function (value: number, index: number) {
					if (isEmptyData) {
						return value === 0 ? '0' : '-'
					}
					if (index % 2 !== 0) {
						return ''
					}
					return Math.round(value).toString()
				},
			},
		},
		tooltip: {
			fixed: true,
			custom: ({ dataPointIndex }) => {
				const timeValue = moment(chartTimestamps[dataPointIndex]).format(
					'DD.MM.YY',
				)
				const sourceDots = normalizedDotsInfo
				const isNoDataPoint =
					isEmptyData ||
					sourceDots.every(el => el.category[dataPointIndex] == null)

				if (isNoDataPoint) {
					const scoreRows =
						selectTypeOfChart === LamenessTypeChart.PointType
							? sourceDots
									.filter(
										(dots, index) => dots.score !== 1 && visibleSeries[index],
									)
									.map(
										el => `<div style='display:flex; flex-direction:row; align-items: center; gap:4px'>
								<span style='width:8px; height:8px; border-radius: 50%; background-color: ${el.color};'></span>
								<p style='font-weight: 400; font-size: 12px; color: #7F7F84; margin: 0px'>Нет данных</p>
							</div>`,
									)
									.join('')
							: `<p style='font-weight: 400; font-size: 12px; color: #7F7F84; margin: 0px'>Нет данных</p>`

					return `<div
				class='lameness-chart-tooltip-no-data'
				style='width:max-content; height:auto; background-color: #fff; border-radius:12px; border: hidden; box-shadow: 0px 0px 16px 0px rgba(4, 6, 6, 0.16); position: relative'>
				<div style='padding:12px 12px; border: none; display:flex; flex-direction:column; gap:4px;'>
					${scoreRows}
					<p style='font-weight: 400; font-size: 12px; color: #7F7F84; margin: 0px'>${timeValue}</p>
				</div>
			</div>`
				}

				const totalCattle = sourceDots.reduce(
					(acc, el) => acc + (el.category[dataPointIndex] || 0),
					0,
				)
				const pointsHtml =
					selectTypeOfChart === LamenessTypeChart.LamenessType
						? (() => {
								const lameCount = sourceDots
									.filter(el => Number(el.score) >= 2 && Number(el.score) <= 5)
									.reduce(
										(acc, el) => acc + Number(el.category[dataPointIndex] || 0),
										0,
									)
								const healthyCount = sourceDots
									.filter(el => Number(el.score) === 1)
									.reduce(
										(acc, el) => acc + Number(el.category[dataPointIndex] || 0),
										0,
									)
								const countRow = (
									value: number,
									label: string,
									showDot: boolean,
								) => `<div style='display:flex; flex-direction:row; align-items: center; gap:4px'>
								${
									showDot
										? `<span style='width:8px; height:8px; border-radius: 50%; background-color: #000;'></span>`
										: ''
								}
								<p style='font-weight: 400; font-size: 12px; color: #7F7F84; margin: 0px'>${label}</p>
								<p style='font-weight: 600; font-size: 16px; color: #000; margin: 0px'>${value.toLocaleString(
									'ru-RU',
								)}</p>
								<p style='font-weight: 400; font-size: 12px; color: #7F7F84; margin: 0px'>голов</p>
							</div>`
								return (
									countRow(lameCount, 'Хромые', true) +
									countRow(healthyCount, 'Здоровые', false)
								)
							})()
						: sourceDots
								.filter(
									(dots, index) => dots.score !== 1 && visibleSeries[index],
								)
								.map(el => {
									const categoryValue = el.category[dataPointIndex] ?? 0
									const percent =
										totalCattle > 0 ? (categoryValue / totalCattle) * 100 : 0
									const color = getColorByScoreForCrytical(el.score, percent)
									return `<div style='display:flex; flex-direction:row; align-items: center; gap:4px'>
								<span style='width:12px; height:12px; border-radius: 50%; background-color: ${
									el.color
								};'></span>
								<p style='font-weight: 400; font-size: 12px; color: #7F7F84; margin: 0px'>${
									el.label
								}</p>
								<p style='font-weight: 600; font-size: 16px; color: #000; margin: 0px'>${categoryValue}</p>
								<p style='font-weight: 400; font-size: 12px; color: #7F7F84; margin: 0px'>${morphForWords(
									categoryValue,
									['голова', 'головы', 'голов'],
								)}</p>
								<span style='font-weight: 600; font-size: 16px; margin: 0px'>/</span>
								<p style='font-weight: 400; font-size: 14px; color: ${color}; margin: 0px'>${
									percent > 0 ? roundingNumber(percent, 1) : '-'
								}</p>
								<span style='font-weight: 400; font-size: 12px; margin: 0px; color: #7F7F84;'>${
									percent > 0 ? '%' : ''
								}</span>
							</div>`
								})
								.join('')

				return `<div
				style='width:max-content; height:auto; background-color: #fff; border-radius:12px; border: hidden; box-shadow: 0px 0px 16px 0px rgba(4, 6, 6, 0.16); position: relative'>
				<div style='padding:16px 8px; border: none;'>
					<div style='display: flex; flex-direction: column; gap:4px'>
						${pointsHtml}
						<div>
							<p style='font-weight: 400; font-size: 12px; color: #7F7F84; margin: 0px'>${timeValue}</p>
						</div>
					</div>
				</div>
			</div>`
			},
		},
		legend: {
			show: false,
		},
	}

	const descriptionForLegend = [
		{ color: COLORS.black, description: 'Количество хромых голов' },
		{ color: COLORS.inputBorder, description: 'Нет данных' },
	]
	const noDataLegend = (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'row',
				gap: '8px',
				alignItems: 'center',
			}}
		>
			<span
				style={{
					backgroundColor: COLORS.inputBorder,
					width: '16px',
					height: '16px',
					borderRadius: '50%',
				}}
			/>
			<TypographySecondary14>Нет данных</TypographySecondary14>
		</Box>
	)
	const icon = <CheckBox />

	return (
		<Box
			sx={{
				'& .lameness-chart-tooltip-no-data': {
					transform: 'translateY(104px)',
				},
			}}
		>
			<ReactApexChart
				key={`lameness-chart-${selectTypeOfChart}-${isEmptyData}`}
				id={'lamenessChart'}
				options={options}
				series={chartSeries}
				type='line'
				height={250}
			/>
			{selectTypeOfChart === LamenessTypeChart.LamenessType && (
				<ChartLegend initialLegends={descriptionForLegend} />
			)}
			{selectTypeOfChart === LamenessTypeChart.PointType && (
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'row',
						gap: '16px',
						alignItems: 'center',
					}}
				>
					{dotsInfo
						.filter(point => point.score !== 1)
						.map((el, index) => (
							<Box
								key={el.score}
								sx={{
									display: 'flex',
									flexDirection: 'row',
									alignItems: 'center',
								}}
							>
								<Checkbox
									onChange={() => handleCheckboxChange(index + 1)}
									icon={icon}
									checkedIcon={
										<CheckBoxActive lineColor={el.color} bgColor={el.color} />
									}
									disableRipple
									style={{
										padding: '0px 8px 0px 0px',
										margin: '0px',
									}}
									checked={visibleSeries[index + 1]}
								/>
								<TypographySecondary14>{el.label}</TypographySecondary14>
							</Box>
						))}
					{noDataLegend}
				</Box>
			)}
		</Box>
	)
}

export default LamenessChart
